import React, { useEffect, useState } from "react";
import axios from "axios";
import { FiDownload, FiSearch, FiStar, FiLock, FiEye, FiX } from "react-icons/fi";
import { motion, AnimatePresence } from "framer-motion";
import DOMPurify from "dompurify";

const Notes = () => {
  const [notes, setNotes] = useState([]);
  const [filteredNotes, setFilteredNotes] = useState([]);
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("All");
  const [favorites, setFavorites] = useState(() =>
    JSON.parse(localStorage.getItem("favoriteNotes") || "[]")
  );
  const [selectedNote, setSelectedNote] = useState(null);
  const API_BASE = process.env.REACT_APP_API_BASE_URL;
  const user = JSON.parse(localStorage.getItem("user"));
  const isPremium = user?.hasPaid;

  // ================= FETCH NOTES =================
  const fetchNotes = async () => {
    try {
      const res = await axios.get(`${API_BASE}/api/user/notes`);
      setNotes(res.data);
    } catch (error) {
      console.error("Failed to load notes:", error);
    }
  };

  useEffect(() => {
    fetchNotes();
  }, []);

  // ================= FILTER =================
  useEffect(() => {
    let filtered = notes;

    if (category !== "All") {
      filtered = filtered.filter((note) => {
        if (category === "Prelims") {
          return note.category === "Prelims" || note.category === "Prelims+Mains";
        }
        if (category === "Mains") {
          return note.category === "Mains" || note.category === "Prelims+Mains";
        }
        return note.category === category;
      });
    }

    if (search.trim()) {
      const query = search.toLowerCase();
      filtered = filtered.filter(
        (note) =>
          note.title.toLowerCase().includes(query) ||
          note.category.toLowerCase().includes(query)
      );
    }

    setFilteredNotes(filtered);
  }, [search, category, notes]);

  // ================= FAVORITES =================
  const toggleFavorite = (id) => {
    let updated = favorites.includes(id)
      ? favorites.filter((fid) => fid !== id)
      : [...favorites, id];
    setFavorites(updated);
    localStorage.setItem("favoriteNotes", JSON.stringify(updated));
  };

  const isFavorite = (id) => favorites.includes(id);

  const loadRazorpay = (src) =>
    new Promise((resolve) => {
      const script = document.createElement("script");
      script.src = src;
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });

  // ================= TRACK ANALYTICS =================
  const trackNoteActivity = async (noteId) => {
    try {
      if (!user?.token) return false;
      const res = await axios.post(
        `${API_BASE}/api/user/notes/track-read`,
        { noteId },
        { headers: { Authorization: `Bearer ${user.token}` } }
      );
      if (!res.data?.duplicate) {
        window.dispatchEvent(
          new CustomEvent("xpGained", {
            detail: { xp: 5, message: "New Note Studied" },
          })
        );
      }
      return true;
    } catch (err) {
      console.error("Note analytics failed", err);
      return false;
    }
  };

  // ================= PAYMENT =================
  const handleLockedClick = async () => {
    const res = await loadRazorpay("https://checkout.razorpay.com/v1/checkout.js");
    if (!res) { alert("Razorpay SDK failed to load"); return; }
    try {
      const orderRes = await axios.post(
        `${API_BASE}/api/payments/create-order`,
        { amount: 3999, type: "notesPremium" },
        { headers: { Authorization: `Bearer ${user.token}` } }
      );
      const { order } = orderRes.data;
      const options = {
        key: process.env.REACT_APP_RAZORPAY_KEY,
        amount: order.amount,
        currency: order.currency,
        name: "UPSC Journey",
        description: "Premium Access",
        order_id: order.id,
        handler: async function (response) {
          try {
            await axios.post(
              `${API_BASE}/api/payments/verify-payment`,
              {
                razorpay_order_id: order.id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
                type: "notesPremium",
              },
              { headers: { Authorization: `Bearer ${user.token}` } }
            );
            alert("✅ Payment Successful! Premium Activated");
            const updatedUser = { ...user, hasPaid: true };
            localStorage.setItem("user", JSON.stringify(updatedUser));
            window.dispatchEvent(new Event("userUpdated"));
            window.location.reload();
          } catch {
            alert("Payment verification failed");
          }
        },
        prefill: { email: user.email, name: user.username },
        theme: { color: "#facc15" },
      };
      const paymentObject = new window.Razorpay(options);
      paymentObject.open();
    } catch (err) {
      alert("Payment failed. Try again.");
    }
  };

  const handleNoteOpen = async (url) => {
    try { await trackNoteActivity(); } catch (err) { console.error(err); }
    window.open(url, "_blank");
  };

  const CATEGORIES = ["All", "Prelims", "Mains", "Prelims + Mains", "Extra"];

  const categoryColors = {
    Prelims: { bg: "bg-violet-500/20", text: "text-violet-300", border: "border-violet-500/30" },
    Mains: { bg: "bg-emerald-500/20", text: "text-emerald-300", border: "border-emerald-500/30" },
    "Prelims+Mains": { bg: "bg-amber-500/20", text: "text-amber-300", border: "border-amber-500/30" },
    Extra: { bg: "bg-rose-500/20", text: "text-rose-300", border: "border-rose-500/30" },
    All: { bg: "bg-sky-500/20", text: "text-sky-300", border: "border-sky-500/30" },
  };

  const getCategoryStyle = (cat) =>
    categoryColors[cat] || { bg: "bg-slate-500/20", text: "text-slate-300", border: "border-slate-500/30" };

  return (
    <div
      className="min-h-screen px-4 py-10 md:px-8"
      style={{
        background: "linear-gradient(135deg, #0a0f1e 0%, #0d1a2e 50%, #0a0f1e 100%)",
        fontFamily: "'DM Sans', sans-serif",
      }}
    >
      {/* Ambient glows */}
      <div className="pointer-events-none fixed inset-0 overflow-hidden">
        <div
          style={{
            position: "absolute", top: "-10%", left: "20%",
            width: "500px", height: "500px",
            background: "radial-gradient(circle, rgba(99,102,241,0.12) 0%, transparent 70%)",
            borderRadius: "50%",
          }}
        />
        <div
          style={{
            position: "absolute", bottom: "10%", right: "10%",
            width: "400px", height: "400px",
            background: "radial-gradient(circle, rgba(20,184,166,0.08) 0%, transparent 70%)",
            borderRadius: "50%",
          }}
        />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto space-y-10">

        {/* ================= HEADER ================= */}
        <motion.div
          initial={{ opacity: 0, y: -24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="flex flex-col xl:flex-row xl:items-end xl:justify-between gap-8"
        >
          {/* LEFT */}
          <div>
            <div className="flex items-center gap-3 mb-1">
              <span
                className="text-xs font-bold tracking-[0.2em] uppercase px-3 py-1 rounded-full"
                style={{
                  background: "rgba(99,102,241,0.2)",
                  color: "#a5b4fc",
                  border: "1px solid rgba(99,102,241,0.3)",
                }}
              >
                UPSC Journey
              </span>
            </div>
            <h1
              className="text-4xl md:text-5xl font-black tracking-tight"
              style={{
                background: "linear-gradient(135deg, #e2e8f0 0%, #a5b4fc 50%, #67e8f9 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
                lineHeight: 1.15,
              }}
            >
              Notes Library
            </h1>
            <p className="mt-2 text-slate-400 text-sm max-w-md">
              Curated study materials, revision notes & UPSC resources — all in one place.
            </p>
          </div>

          {/* RIGHT — Controls */}
          <div className="flex flex-col sm:flex-row gap-3 w-full xl:w-auto">
            {/* Category Select */}
            <div className="relative">
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="appearance-none w-full sm:w-44 pl-4 pr-10 py-3 rounded-2xl text-sm font-medium transition-all outline-none cursor-pointer"
                style={{
                  background: "rgba(255,255,255,0.05)",
                  border: "1px solid rgba(255,255,255,0.1)",
                  color: "#e2e8f0",
                  backdropFilter: "blur(12px)",
                  boxShadow: "0 4px 24px rgba(0,0,0,0.2)",
                }}
              >
                {CATEGORIES.map((c) => (
                  <option key={c} value={c} style={{ background: "#0d1a2e" }}>
                    {c === "All" ? "All Categories" : c}
                  </option>
                ))}
              </select>
              <div className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">▾</div>
            </div>

            {/* Search */}
            <div className="relative flex-1 sm:w-80">
              <FiSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search by title or category…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-11 pr-4 py-3 rounded-2xl text-sm outline-none transition-all"
                style={{
                  background: "rgba(255,255,255,0.05)",
                  border: "1px solid rgba(255,255,255,0.1)",
                  color: "#e2e8f0",
                  backdropFilter: "blur(12px)",
                  boxShadow: "0 4px 24px rgba(0,0,0,0.2)",
                }}
                onFocus={(e) => (e.target.style.border = "1px solid rgba(99,102,241,0.5)")}
                onBlur={(e) => (e.target.style.border = "1px solid rgba(255,255,255,0.1)")}
              />
            </div>
          </div>
        </motion.div>

        {/* Results count */}
        {filteredNotes.length > 0 && (
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-xs text-slate-500 -mt-4"
          >
            Showing <span className="text-slate-300 font-semibold">{filteredNotes.length}</span> note{filteredNotes.length !== 1 ? "s" : ""}
          </motion.p>
        )}

        {/* ================= NOTES GRID ================= */}
        <AnimatePresence>
          {filteredNotes.length > 0 ? (
            <motion.div
              layout
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 gap-6"
            >
              {filteredNotes.map((note, i) => {
                const isLocked = !isPremium && !note.isFree;
                const catStyle = getCategoryStyle(note.category);

                return (
                  <motion.div
                    key={note._id}
                    layout
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.95 }}
                    transition={{ duration: 0.35, delay: i * 0.04 }}
                    className="group relative flex flex-col rounded-3xl overflow-hidden"
                    style={{
                      background: "linear-gradient(145deg, #243156 0%, #1a2744 100%)",
                      border: isLocked ? "1px solid rgba(251,191,36,0.5)" : "1px solid rgba(99,102,241,0.45)",
                      backdropFilter: "blur(20px)",
                      boxShadow: isLocked ? "0 8px 32px rgba(0,0,0,0.4), 0 0 20px rgba(251,191,36,0.08)" : "0 8px 32px rgba(0,0,0,0.4), 0 0 20px rgba(99,102,241,0.12)",
                      transition: "transform 0.3s ease, box-shadow 0.3s ease",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = "translateY(-6px)";
                      e.currentTarget.style.boxShadow = isLocked
                        ? "0 20px 60px rgba(0,0,0,0.5), 0 0 0 1px rgba(251,191,36,0.3)"
                        : "0 20px 60px rgba(0,0,0,0.5), 0 0 0 1px rgba(99,102,241,0.2)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = "translateY(0)";
                      e.currentTarget.style.boxShadow = isLocked ? "0 8px 32px rgba(0,0,0,0.3), 0 0 0 1px rgba(251,191,36,0.06)" : "0 8px 32px rgba(0,0,0,0.3)";
                    }}
                  >
                    {/* THUMBNAIL */}
                    <div
                      className="relative h-52 flex items-center justify-center overflow-hidden"
                      style={{
                        background: "linear-gradient(135deg, #2d3a6b 0%, #1a3a4a 100%)",
                      }}
                    >
                      {note.thumbnailUrl ? (
                        <img
                          src={note.thumbnailUrl}
                          alt="thumbnail"
                          className="w-full h-full object-contain transition-transform duration-500 group-hover:scale-105"
                        />
                      ) : (
                        <div className="flex flex-col items-center gap-2 text-slate-600">
                          <span className="text-4xl">📄</span>
                          <span className="text-xs">No Preview</span>
                        </div>
                      )}
                      {/* gradient overlay on thumb */}
                      <div
                        className="absolute inset-0"
                        style={{
                          background: "linear-gradient(to top, rgba(10,15,30,0.8) 0%, transparent 50%)",
                        }}
                      />
                    </div>

                    {/* PREMIUM BADGE on thumbnail (only for locked) — no full overlay */}
                    {isLocked && (
                      <div
                        className="absolute top-3 left-3 z-20 flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold"
                        style={{
                          background: "rgba(251,191,36,0.18)",
                          border: "1px solid rgba(251,191,36,0.4)",
                          color: "#fbbf24",
                          backdropFilter: "blur(8px)",
                        }}
                      >
                        <FiLock className="w-3 h-3" /> Premium
                      </div>
                    )}

                    {/* FAVORITE BUTTON */}
                    <button
                      onClick={() => toggleFavorite(note._id)}
                      className="absolute top-3 right-3 z-20 w-9 h-9 rounded-full flex items-center justify-center transition-all active:scale-90"
                      style={{
                        background: "rgba(10,15,30,0.6)",
                        backdropFilter: "blur(8px)",
                        border: "1px solid rgba(255,255,255,0.1)",
                      }}
                    >
                      <FiStar
                        className="w-4 h-4 transition-all"
                        style={{
                          color: isFavorite(note._id) ? "#fbbf24" : "#94a3b8",
                          fill: isFavorite(note._id) ? "#fbbf24" : "none",
                          filter: isFavorite(note._id) ? "drop-shadow(0 0 6px rgba(251,191,36,0.6))" : "none",
                        }}
                      />
                    </button>

                    {/* CARD CONTENT — always fully visible */}
                    <div className="flex flex-col flex-1 p-5">
                      {/* Category pill */}
                      <div className="mb-3">
                        <span
                          className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-bold ${catStyle.bg} ${catStyle.text}`}
                          style={{ border: `1px solid`, borderColor: catStyle.border.replace("border-", "").replace("/30", "").replace("-", " ") }}
                        >
                          {note.category}
                        </span>
                      </div>

                      <h3 className="text-base font-bold text-white leading-snug mb-2" style={{ textShadow: "0 0 20px rgba(165,180,252,0.3)" }}>
                        {note.title}
                      </h3>

                      {/* Description — always readable, Read more always clickable */}
                      <div className="flex-1">
                        <div
                          className="text-xs leading-6 text-slate-300 line-clamp-4"
                          dangerouslySetInnerHTML={{
                            __html: DOMPurify.sanitize(note.description || ""),
                          }}
                        />
                        {(note.description || "").length > 180 && (
                          <button
                            onClick={() => setSelectedNote(note)}
                            className="mt-2 text-xs font-semibold transition-all"
                            style={{ color: "#818cf8" }}
                            onMouseEnter={(e) => (e.currentTarget.style.color = "#a5b4fc")}
                            onMouseLeave={(e) => (e.currentTarget.style.color = "#818cf8")}
                          >
                            Read more →
                          </button>
                        )}
                      </div>

                      {/* Date */}
                      <p className="text-[10px] text-slate-600 mt-3 mb-4">
                        {new Date(note.publishDate || note.createdAt).toLocaleDateString("en-IN", {
                          day: "numeric", month: "short", year: "numeric",
                        })}
                      </p>

                      {/* ACTIONS */}
                      {!isLocked ? (
                        <div className="flex gap-2 mt-auto">
                          {/* View */}
                          <button
                            onClick={async () => {
                              await trackNoteActivity(note._id);
                              window.open(note.fileUrl, "_blank");
                            }}
                            className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-semibold transition-all active:scale-95"
                            style={{
                              background: "rgba(255,255,255,0.07)",
                              border: "1px solid rgba(255,255,255,0.1)",
                              color: "#cbd5e1",
                            }}
                            onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(255,255,255,0.12)")}
                            onMouseLeave={(e) => (e.currentTarget.style.background = "rgba(255,255,255,0.07)")}
                          >
                            <FiEye className="w-4 h-4" /> View
                          </button>
                          {/* Download */}
                          <button
                            onClick={async () => {
                              await trackNoteActivity(note._id);
                              const link = document.createElement("a");
                              link.href = note.fileUrl;
                              link.download = "";
                              document.body.appendChild(link);
                              link.click();
                              document.body.removeChild(link);
                            }}
                            className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-bold transition-all active:scale-95"
                            style={{
                              background: "linear-gradient(135deg, #6366f1, #4f46e5)",
                              color: "#fff",
                              boxShadow: "0 4px 16px rgba(99,102,241,0.35)",
                            }}
                            onMouseEnter={(e) => (e.currentTarget.style.boxShadow = "0 6px 24px rgba(99,102,241,0.55)")}
                            onMouseLeave={(e) => (e.currentTarget.style.boxShadow = "0 4px 16px rgba(99,102,241,0.35)")}
                          >
                            <FiDownload className="w-4 h-4" /> Download
                          </button>
                        </div>
                      ) : (
                        /* Locked action row — upgrade CTA instead of buttons */
                        <div className="mt-auto flex gap-2">
                          <button
                            onClick={handleLockedClick}
                            className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-bold transition-all active:scale-95"
                            style={{
                              background: "linear-gradient(135deg, rgba(251,191,36,0.15), rgba(251,191,36,0.08))",
                              border: "1px solid rgba(251,191,36,0.35)",
                              color: "#fbbf24",
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.background = "linear-gradient(135deg, rgba(251,191,36,0.25), rgba(251,191,36,0.15))";
                              e.currentTarget.style.borderColor = "rgba(251,191,36,0.6)";
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.background = "linear-gradient(135deg, rgba(251,191,36,0.15), rgba(251,191,36,0.08))";
                              e.currentTarget.style.borderColor = "rgba(251,191,36,0.35)";
                            }}
                          >
                            <FiLock className="w-3.5 h-3.5" /> Unlock to Access
                          </button>
                        </div>
                      )}
                    </div>
                  </motion.div>
                );
              })}
            </motion.div>
          ) : (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="flex flex-col items-center justify-center py-24 text-center"
            >
              <div
                className="w-16 h-16 rounded-2xl flex items-center justify-center mb-4 text-2xl"
                style={{ background: "rgba(99,102,241,0.15)", border: "1px solid rgba(99,102,241,0.2)" }}
              >
                🔍
              </div>
              <p className="text-slate-300 font-semibold text-lg">No notes found</p>
              <p className="text-slate-500 text-sm mt-1">Try a different search or category</p>
            </motion.div>
          )}
        </AnimatePresence>

        {/* ================= NOTE DETAILS MODAL ================= */}
        <AnimatePresence>
          {selectedNote && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="fixed inset-0 z-50 flex items-end sm:items-center justify-center sm:p-4"
              style={{ background: "rgba(0,0,0,0.8)", backdropFilter: "blur(16px)" }}
              onClick={() => setSelectedNote(null)}
            >
              <motion.div
                initial={{ opacity: 0, y: 60 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 40 }}
                transition={{ duration: 0.3, type: "spring", bounce: 0.18 }}
                className="relative w-full sm:max-w-2xl max-h-[95vh] sm:max-h-[88vh] overflow-y-auto rounded-t-3xl sm:rounded-3xl"
                style={{
                  background: "#080e1c",
                  border: "1px solid rgba(99,102,241,0.18)",
                  boxShadow: "0 0 0 1px rgba(99,102,241,0.1), 0 40px 120px rgba(0,0,0,0.7)",
                }}
                onClick={(e) => e.stopPropagation()}
              >

                {/* ── HERO THUMBNAIL ── */}
                <div
                  className="relative w-full overflow-hidden rounded-t-3xl"
                  style={{ height: "300px" }}
                >
                  {selectedNote.thumbnailUrl ? (
                    <motion.img
                      src={selectedNote.thumbnailUrl}
                      alt={selectedNote.title}
                      initial={{ scale: 1.08 }}
                      animate={{ scale: 1 }}
                      transition={{ duration: 0.55, ease: "easeOut" }}
                      className="w-full h-full object-contain"
                    />
                  ) : (
                    <div
                      className="w-full h-full flex flex-col items-center justify-center gap-3"
                      style={{
                        background: "linear-gradient(135deg, rgba(99,102,241,0.25) 0%, rgba(20,184,166,0.18) 100%)",
                      }}
                    >
                      <span className="text-6xl">📚</span>
                      <span className="text-slate-500 text-sm font-medium">No preview available</span>
                    </div>
                  )}

                  {/* Deep gradient fade — title bleeds into it */}
                  <div
                    className="absolute inset-0"
                    style={{
                      background: "linear-gradient(to top, #080e1c 0%, rgba(8,14,28,0.55) 45%, rgba(8,14,28,0.1) 100%)",
                    }}
                  />

                  {/* Title & meta pinned to bottom of hero */}
                  <div className="absolute bottom-0 left-0 right-0 px-7 pb-6 z-10">
                    {(() => {
                      const catStyle = getCategoryStyle(selectedNote.category);
                      return (
                        <span
                          className={`inline-flex items-center px-3 py-1 rounded-full text-[11px] font-bold mb-3 ${catStyle.bg} ${catStyle.text}`}
                          style={{ border: "1px solid rgba(255,255,255,0.08)" }}
                        >
                          {selectedNote.category}
                        </span>
                      );
                    })()}
                    <h2
                      className="text-2xl md:text-[1.75rem] font-black leading-tight text-white"
                      style={{ textShadow: "0 2px 16px rgba(0,0,0,0.7)" }}
                    >
                      {selectedNote.title}
                    </h2>
                    <p className="mt-1.5 text-[11px] text-slate-400 flex items-center gap-1.5">
                      <span
                        className="inline-block w-1.5 h-1.5 rounded-full"
                        style={{ background: "#6366f1" }}
                      />
                      Published{" "}
                      {new Date(
                        selectedNote.publishDate || selectedNote.createdAt
                      ).toLocaleDateString("en-IN", {
                        day: "numeric", month: "long", year: "numeric",
                      })}
                    </p>
                  </div>

                  {/* Close button */}
                  <button
                    onClick={() => setSelectedNote(null)}
                    className="absolute top-4 right-4 z-20 w-9 h-9 rounded-full flex items-center justify-center transition-all hover:scale-110 active:scale-90"
                    style={{
                      background: "rgba(0,0,0,0.5)",
                      backdropFilter: "blur(8px)",
                      border: "1px solid rgba(255,255,255,0.12)",
                      color: "#94a3b8",
                    }}
                  >
                    <FiX className="w-4 h-4" />
                  </button>
                </div>

                {/* ── BODY ── */}
                <div className="px-7 pt-6 pb-10">

                  {/* Thin accent rule */}
                  <div
                    className="mb-6"
                    style={{
                      height: "1px",
                      background: "linear-gradient(90deg, rgba(99,102,241,0.5), rgba(20,184,166,0.3), transparent)",
                    }}
                  />

                  {/* Description with styled HTML */}
                  <div
                    className="prose-modal"
                    style={{
                      fontSize: "15px",
                      lineHeight: "1.85",
                      color: "#94a3b8",
                      wordBreak: "break-word",
                    }}
                    dangerouslySetInnerHTML={{
                      __html: DOMPurify.sanitize(selectedNote.description || ""),
                    }}
                  />

                  {/* Bottom fade hint if content is long */}
                </div>

                {/* Scoped styles for rendered HTML content */}
                <style>{`
                  .prose-modal h1, .prose-modal h2, .prose-modal h3 {
                    color: #e2e8f0;
                    font-weight: 700;
                    margin-top: 1.4em;
                    margin-bottom: 0.5em;
                    line-height: 1.3;
                  }
                  .prose-modal h1 { font-size: 1.35rem; }
                  .prose-modal h2 { font-size: 1.15rem; border-bottom: 1px solid rgba(99,102,241,0.2); padding-bottom: 0.3em; }
                  .prose-modal h3 { font-size: 1rem; color: #a5b4fc; }
                  .prose-modal p { margin-bottom: 0.9em; }
                  .prose-modal strong, .prose-modal b { color: #e2e8f0; font-weight: 600; }
                  .prose-modal em { color: #c7d2fe; font-style: italic; }
                  .prose-modal ul, .prose-modal ol { padding-left: 1.4em; margin-bottom: 0.9em; }
                  .prose-modal li { margin-bottom: 0.35em; }
                  .prose-modal ul li::marker { color: #6366f1; }
                  .prose-modal ol li::marker { color: #6366f1; font-weight: 600; }
                  .prose-modal a { color: #818cf8; text-decoration: underline; text-underline-offset: 3px; }
                  .prose-modal blockquote {
                    border-left: 3px solid #6366f1;
                    margin: 1em 0;
                    padding: 0.6em 1em;
                    background: rgba(99,102,241,0.08);
                    border-radius: 0 8px 8px 0;
                    color: #a5b4fc;
                    font-style: italic;
                  }
                  .prose-modal code {
                    background: rgba(99,102,241,0.15);
                    color: #a5b4fc;
                    padding: 2px 6px;
                    border-radius: 4px;
                    font-size: 0.85em;
                  }
                  .prose-modal hr {
                    border: none;
                    border-top: 1px solid rgba(255,255,255,0.07);
                    margin: 1.4em 0;
                  }
                `}</style>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

      </div>
    </div>
  );
};

export default Notes;
